import { useEffect, useId, useRef, useState } from "react";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@akuna/ui";
import { DateFieldPopover } from "@/components/ui/date-field-popover";
import { Input } from "@akuna/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMaskedDigitsCaret } from "@/hooks/useMaskedDigitsCaret";
import {
  convertDisplayToISO,
  formatISOToDisplay,
  getBirthDateFromPetAge,
  getDateErrorMessage,
  getPetAgeValueFromBirthDate,
  maskDateInput,
  type PetAgeUnit,
} from "@/lib/date-utils";
import { cn } from "@/lib/utils";

type PetBirthDateAgeFieldProps = {
  value?: string;
  onChange: (value: string) => void;
  calendarButtonClassName?: string;
  inputClassName?: string;
  /** When true, render only the birth date input + calendar. When false with showAgeOnly, render only age. */
  showBirthDateOnly?: boolean;
  /** When true, render only the age input + unit select. When false with showBirthDateOnly, render only birth date. */
  showAgeOnly?: boolean;
};

export function PetBirthDateAgeField({
  value,
  onChange,
  calendarButtonClassName,
  inputClassName,
  showBirthDateOnly = false,
  showAgeOnly = false,
}: PetBirthDateAgeFieldProps) {
  const { t } = useTranslation("patients");
  const [birthDateInput, setBirthDateInput] = useState("");
  const [isBirthDateCalendarOpen, setIsBirthDateCalendarOpen] = useState(false);
  const [ageValue, setAgeValue] = useState("");
  const [ageUnit, setAgeUnit] = useState<PetAgeUnit>("years");
  const { inputRef: birthDateInputRef, captureCaret: captureBirthDateCaret } =
    useMaskedDigitsCaret(birthDateInput);
  const ageSelectId = useId();
  // Tracks the ISO value this component last emitted via onChange, so the value
  // echoed back through props (e.g. while typing an incomplete date, which reports
  // "") doesn't clobber what the user is still typing.
  const lastEmittedValueRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastEmittedValueRef.current === (value ?? "")) {
      lastEmittedValueRef.current = null;
      return;
    }

    const nextBirthDateInput = formatISOToDisplay(value);
    setBirthDateInput((current) =>
      current === nextBirthDateInput ? current : nextBirthDateInput,
    );

    const age = getPetAgeValueFromBirthDate(value);
    if (!age) {
      setAgeValue("");
      setAgeUnit("years");
      return;
    }

    setAgeValue((current) => (current === age.value ? current : age.value));
    setAgeUnit((current) => (current === age.unit ? current : age.unit));
  }, [value]);

  const dateError = getDateErrorMessage(birthDateInput);
  const showBirthDate = showBirthDateOnly || (!showBirthDateOnly && !showAgeOnly);
  const showAge = showAgeOnly || (!showBirthDateOnly && !showAgeOnly);

  const ageUnitOptions: Array<{ value: PetAgeUnit; label: string }> = [
    { value: "years", label: t("birthDateAge.units.years") },
    { value: "months", label: t("birthDateAge.units.months") },
    { value: "days", label: t("birthDateAge.units.days") },
  ];

  return (
    <div>
      <div className="flix">
        {showBirthDate && (
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("birthDateAge.datePlaceholder")}
            inputMode="numeric"
            ref={birthDateInputRef}
            value={birthDateInput}
            onChange={(event) => {
              const rawValue = event.target.value;
              captureBirthDateCaret(rawValue, event.target.selectionStart);

              const maskedValue = maskDateInput(rawValue);
              setBirthDateInput(maskedValue);
              const isoValue = convertDisplayToISO(maskedValue) || "";
              lastEmittedValueRef.current = isoValue;
              onChange(isoValue);
            }}
            onBlur={() => {
              const isoValue = convertDisplayToISO(birthDateInput);
              if (!isoValue) {
                lastEmittedValueRef.current = "";
                onChange("");
                setBirthDateInput("");
              }
            }}
            className={cn("!flex-1", inputClassName, dateError && "border-destructive")}
          />
          <DateFieldPopover
            open={isBirthDateCalendarOpen}
            onOpenChange={setIsBirthDateCalendarOpen}
            trigger={
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={cn("shrink-0", calendarButtonClassName)}
                aria-label={t("birthDateAge.selectBirthDate")}
              >
                <CalendarIcon className="h-4 w-4" />
              </Button>
            }
            selected={
              value
                ? (() => {
                  const [year, month, day] = value.split("-");
                  if (!year || !month || !day) return undefined;
                  return new Date(Number(year), Number(month) - 1, Number(day));
                })()
                : undefined
            }
            onSelect={(date) => {
              if (!date) return;
              const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
              onChange(iso);
              setBirthDateInput(formatISOToDisplay(iso));
            }}
            disabled={(date) => date > new Date()}
          />
        </div>
        )}

        {showAge && (
        <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-2">
          <Input
            placeholder={t("birthDateAge.agePlaceholder")}
            inputMode="numeric"
            value={ageValue}
            onChange={(event) => {
              const nextValue = event.target.value.replace(/\D/g, "").slice(0, 3);
              setAgeValue(nextValue);
              onChange(getBirthDateFromPetAge(nextValue, ageUnit));
            }}
            aria-label={t("birthDateAge.ageLabel")}
            aria-describedby={dateError ? `${ageSelectId}-date-error` : undefined}
          />
          <Select
            value={ageUnit}
            onValueChange={(nextValue: PetAgeUnit) => {
              setAgeUnit(nextValue);
              onChange(getBirthDateFromPetAge(ageValue, nextValue));
            }}
          >
            <SelectTrigger id={ageSelectId} aria-label={t("birthDateAge.ageUnitLabel")}>
              <SelectValue placeholder={t("birthDateAge.ageUnitPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {ageUnitOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        )}
      </div>
      {dateError && (
        <p id={`${ageSelectId}-date-error`} className="text-sm text-destructive">
          {dateError}
        </p>
      )}
    </div>
  );
}
